import { useState, useEffect } from 'react'

import './App.css'

// Utils
import { devLogWarn, devLog } from './utils/devLog'
import { config, configUtils } from './config/app'

// Components
import { ArrangementControls } from './components/ArrangementControls'
import BottomBar from './components/BottomBar'
import ControlPanel from './components/ControlPanel.tsx'
import Fretboard from './components/Fretboard.tsx'
import Row from './components/Row'
import { UpgradePrompt } from './components/account/UpgradePrompt'
import { LoginModal } from './components/account/LoginModal'
import { SubscriptionManager } from './components/account/SubscriptionManager'
import ErrorBoundary from './components/ErrorBoundary'
import { DevDebugPanel } from './components/debug/DevDebugPanel'
import { 
  FretboardErrorBoundary, 
  PaymentErrorBoundary, 
  ControlPanelErrorBoundary,
  BottomBarErrorBoundary 
} from './components/SpecializedErrorBoundaries'

// Auth components
import { AuthProvider } from './auth/AuthProvider'
import { useAuth } from './auth/AuthContext'

// Utils
import { LimitationEngine } from './utils/limitations'

// Cache service
import { preloadCaches } from './data/cacheService'

// Types
import type { 
  GuitarArrangement,
  Note
} from './presets/presetSchema'
import type { ParsedChord } from './utils/chordParser'
import type { SoundType } from './utils/audio'

// Constants and defaults
import { 
  DEFAULT_CAGED_SHAPES,
  DEFAULT_CAGED_VISUALIZATIONS,
  DEFAULT_ROOT_CONFIG
} from './presets/presetSchema'

// localStorage key for current arrangement
const CURRENT_ARRANGEMENT_KEY = 'current_guitar_arrangement'

/**
 * Save the current guitar arrangement to localStorage
 */
function saveArrangementToLocalStorage(guitars: GuitarArrangement[]) {
  try {
    localStorage.setItem(CURRENT_ARRANGEMENT_KEY, JSON.stringify(guitars));
  } catch {
    // Silently handle localStorage errors (quota exceeded, etc.)
  }
}

/**
 * Load the guitar arrangement from localStorage, or return default
 */
function loadArrangementFromLocalStorage(): GuitarArrangement[] {
  try {
    const saved = localStorage.getItem(CURRENT_ARRANGEMENT_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate that it's an array and has the expected structure
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].id) {
        return parsed;
      }
    }
  } catch {
    // Fall back to default if parsing fails
  }
  
  // Return default arrangement
  return [
    {
      id: "1",
      root: {
        ...DEFAULT_ROOT_CONFIG,
      },
      overlay: undefined,
      caged: {
        activeShapes: { ...DEFAULT_CAGED_SHAPES },
        quality: 'MAJOR',
        visualizations: { ...DEFAULT_CAGED_VISUALIZATIONS }
      }
    }
  ];
}

// State management
import {
  addGuitarToArrangement,
  removeGuitarFromArrangement,
  updateGuitarInArrangement
} from './state/guitarState'

// Utilities
import { SOUND_TYPES, setSoundType } from './utils/audio'
import { 
  parseChordSymbolsToGuitars,
  parsedChordsToGuitars
} from './utils/presetHelpers'
import { calculateSemitoneDistance, transposeGuitars } from './utils/transpose'

/**
 * Main application component for the guitar fretboard application.
 * Manages the global state for guitar arrangements and provides 
 * the main UI layout.
 */
function AppContent() {
  // Auth context for limitation checks
  const { user } = useAuth();
  
  // Expose config utils to browser console in development
  useEffect(() => {
    if (configUtils.isDevelopment()) {
      // eslint-disable-next-line no-console
      console.log('🔧 Debug helpers available on window.configUtils');
      (window as any).configUtils = configUtils;
      (window as any).appConfig = config;
      configUtils.logConfig();
    }
  }, []);
  
  // Preload caches on app startup
  useEffect(() => {
    preloadCaches();
  }, []);

  // Handle auth callback (email confirmation, password reset, etc.)
  useEffect(() => {
    const handleAuthCallback = async () => {
      const url = window.location.href;
      const hash = window.location.hash;
      
      devLog('Auth callback check - URL:', url);
      devLog('Auth callback check - hash:', hash);
      
      // Handle URL-encoded hash (from email links)
      if (url.includes('%23access_token=') && !hash) {
        // Extract the URL-encoded hash and decode it
        const encodedHash = url.split('%23')[1];
        if (encodedHash) {
          const decodedHash = decodeURIComponent(encodedHash);
          devLog('Decoded hash from URL:', decodedHash);
          
          // Replace the current URL with the properly formatted hash
          const baseUrl = window.location.origin + window.location.pathname;
          window.history.replaceState(null, '', `${baseUrl}#${decodedHash}`);
          
          // Let Supabase handle the session
          devLog('Auth callback processed - hash URL corrected');
          return;
        }
      }
      
      // Handle normal hash-based auth callback
      const params = new URLSearchParams(hash.substring(1));
      
      devLog('Auth callback params:', {
        hasAccessToken: params.has('access_token'),
        hasRefreshToken: params.has('refresh_token'),
        hasError: params.has('error'),
        errorDescription: params.get('error_description'),
        type: params.get('type')
      });
      
      if (params.has('access_token') || params.has('refresh_token')) {
        try {
          devLog('Processing auth callback with tokens');
          // Supabase will automatically handle the session
          // Just clean up the URL hash after a brief delay to let Supabase process
          setTimeout(() => {
            window.history.replaceState(null, '', window.location.pathname);
          }, 1000);
          devLog('Auth callback processed successfully');
        } catch (error) {
          devLogWarn('Auth callback error:', error);
        }
      } else if (params.has('error')) {
        const errorType = params.get('error');
        const errorDescription = params.get('error_description');
        
        devLogWarn('Auth callback received error:', {
          error: errorType,
          description: errorDescription,
          fullURL: url,
          fullHash: hash
        });
        
        // Show user-friendly error message
        // eslint-disable-next-line no-console
        console.error('🚨 Email confirmation failed:', {
          error: errorType,
          description: errorDescription,
          possibleCauses: [
            'Email link may have expired',
            'Email link may have been used already', 
            'Incorrect redirect URL configuration',
            'Browser blocking the confirmation'
          ]
        });
      }
    };

    handleAuthCallback();
  }, []);
  
  // State management
  const [guitars, setGuitars] = useState<GuitarArrangement[]>(() => 
    loadArrangementFromLocalStorage()
  );
  
  // Save guitars arrangement to localStorage whenever it changes
  useEffect(() => {
    saveArrangementToLocalStorage(guitars);
  }, [guitars]);
  
  const [soundType, setSoundTypeState] = useState<SoundType>(SOUND_TYPES.ACOUSTIC);
  
  // Upgrade prompt state
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const [upgradeMessage, setUpgradeMessage] = useState('');

  // Account modal states
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSubscriptionManager, setShowSubscriptionManager] = useState(false);

  // Helper function to show upgrade prompt
  const showUpgrade = (feature: string, customMessage?: string) => {
    devLog('Showing upgrade prompt', {
      feature,
      customMessage: !!customMessage,
      hasUser: !!user,
      userTier: user?.tier || 'none'
    });
    
    setUpgradeFeature(feature);
    setUpgradeMessage(customMessage || '');
    setShowUpgradePrompt(true);
  };

  const handleUpgrade = async () => {
    devLog('handleUpgrade called', { 
      hasUser: !!user, 
      userId: user?.uid || 'none',
      upgradeFeature 
    });

    if (!user) {
      devLogWarn('User not logged in attempting to upgrade', {
        feature: upgradeFeature,
        action: 'redirecting_to_login_modal'
      });
      
      // Show login modal first
      setShowUpgradePrompt(false);
      setShowLoginModal(true);
      return;
    }

    try {
      devLog('Starting upgrade process for logged in user', {
        userId: user.uid,
        feature: upgradeFeature
      });
      
      setShowUpgradePrompt(false);
      
      // Import the upgrade function
      const { upgradeToPremmium } = await import('./services/stripeService');
      await upgradeToPremmium(user);
    } catch {
      devLogWarn('Upgrade process failed', {
        userId: user?.uid,
        feature: upgradeFeature
      });
      
      // Show error message to user
      alert('Failed to start upgrade process. Please try again.');
    }
  };

  // Guitar arrangement handlers
  const updateGuitarState = (id: string, newArrangement: GuitarArrangement) => {
    setGuitars(updateGuitarInArrangement(guitars, id, newArrangement));
  };
  
  const addGuitar = () => {
    // Check limitation for guitar arrangements
    const userTier = user?.tier || 'free';
    const limitCheck = LimitationEngine.checkGuitarLimit(guitars.length + 1, userTier);
    
    if (!limitCheck.allowed) {
          showUpgrade('guitar-arrangements');
      return;
    }
    
    setGuitars(addGuitarToArrangement(guitars));
  };
  
  const deleteGuitar = (id: string) => {
    setGuitars(removeGuitarFromArrangement(guitars, id));
  };
  
  const moveGuitarUp = (id: string) => {
    const index = guitars.findIndex(g => g.id === id);
    if (index <= 0) return;
    
    const newGuitars = [...guitars];
    [newGuitars[index - 1], newGuitars[index]] = [newGuitars[index], newGuitars[index - 1]];
    setGuitars(newGuitars);
  };
  
  const moveGuitarDown = (id: string) => {
    const index = guitars.findIndex(g => g.id === id);
    if (index === -1 || index >= guitars.length - 1) return;
    
    const newGuitars = [...guitars];
    [newGuitars[index], newGuitars[index + 1]] = [newGuitars[index + 1], newGuitars[index]];
    setGuitars(newGuitars);
  };

  // Audio and interaction handlers
  const handleSoundTypeChange = (newSoundType: string) => {
    const typedSoundType = newSoundType as SoundType;
    setSoundTypeState(typedSoundType);
    setSoundType(typedSoundType);
  };

  const handleTranspose = (targetKey: Note) => {
    if (!guitars || guitars.length === 0) return;
    
    const currentKey = guitars[0].root.note;
    const semitones = calculateSemitoneDistance(currentKey, targetKey);
    
    if (semitones === 0) return; // No change needed
    
    const transposedGuitars = transposeGuitars(guitars, semitones);
    setGuitars(transposedGuitars);
  };

  // Chord progression generation handlers
  const handleGenerateFromChords = (chords: ParsedChord[]) => {
    try {
      // Check chord progression limitation
      const userTier = user?.tier || 'free';
      const chordLimitCheck = LimitationEngine.checkChordProgressionLimit(chords.length, userTier);
      
      if (!chordLimitCheck.allowed) {
        showUpgrade('chord-progressions');
        return;
      }

      // Check if adding these guitars would exceed arrangement limit
      const totalGuitarsAfter = guitars.length + chords.length;
      const guitarLimitCheck = LimitationEngine.checkGuitarLimit(totalGuitarsAfter, userTier);
      
      if (!guitarLimitCheck.allowed) {
        showUpgrade('guitar-arrangements');
        return;
      }

      // Generate new guitars and add them
      const startingId = Math.max(0, ...guitars.map(g => Number.parseInt(g.id, 10))) + 1;
      
      // Check if we're receiving ParsedChord objects or strings
      const isParseChordObjects = chords.length > 0 && 
        typeof chords[0] === 'object' && 
        'root' in chords[0] && 
        'quality' in chords[0];
      
      const newGuitars = isParseChordObjects 
        ? parsedChordsToGuitars(chords, { startingId })
        : parseChordSymbolsToGuitars(chords as unknown as string[], { startingId });

      // Add the new guitars to the existing ones
      setGuitars(prevGuitars => [...prevGuitars, ...newGuitars]);
      
    } catch {
      // Silently handle errors
    }
  };

  const handleReplaceFromArrangements = (arrangements: GuitarArrangement[]) => {
    try {
      // Check if loading these arrangements would exceed guitar count limit
      const userTier = user?.tier || 'free';
      const guitarLimitCheck = LimitationEngine.checkGuitarLimit(arrangements.length, userTier);
      
      if (!guitarLimitCheck.allowed) {
        showUpgrade('guitar-arrangements');
        return;
      }

      // Load the arrangements - UI will automatically lock based on content analysis
      setGuitars(arrangements);
    } catch {
      // Silently handle errors
    }
  };

  return (
    <div className="app-container">
      {guitars.map((guitar) => (
        <Row key={guitar.id}>            
          <ControlPanelErrorBoundary>
            <ControlPanel
                arrangement={guitar}
                setArrangement={(newArrangement: GuitarArrangement) => updateGuitarState(guitar.id, newArrangement)}
                onShowUpgrade={showUpgrade}
              />
          </ControlPanelErrorBoundary>
          
          <FretboardErrorBoundary>
            <Fretboard
              arrangement={guitar}
              soundType={soundType}
            />
          </FretboardErrorBoundary>
          
          <ErrorBoundary>
            <ArrangementControls
              id={guitar.id}
              onMoveUp={moveGuitarUp}
              onMoveDown={moveGuitarDown}
              onDelete={deleteGuitar}
            />
          </ErrorBoundary>
        </Row>
      ))}
      
      <BottomBarErrorBoundary>
        <BottomBar 
          onAddGuitar={addGuitar}
          soundType={soundType}
          onSoundTypeChange={handleSoundTypeChange}
          guitars={guitars}
          onPresetNameChange={() => {}} 
          onGenerateFromChords={handleGenerateFromChords}
          onReplaceFromArrangements={handleReplaceFromArrangements}
          onTranspose={handleTranspose}
          onShowUpgrade={showUpgrade}
          onShowLoginModal={() => setShowLoginModal(true)}
          onShowSubscriptionManager={() => setShowSubscriptionManager(true)}
        />
      </BottomBarErrorBoundary>
      
      {/* Upgrade Prompt Modal */}
      <PaymentErrorBoundary>
        <UpgradePrompt
          isOpen={showUpgradePrompt}
          onClose={() => setShowUpgradePrompt(false)}
          onUpgrade={handleUpgrade}
          feature={upgradeFeature}
          message={upgradeMessage}
        />
      </PaymentErrorBoundary>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        title="fr3t dot net"
        subtitle="Sign in to save your guitar arrangements and access them from any device"
      />

      {/* Subscription Manager Modal */}
      {showSubscriptionManager && (
        <div className="account-modal-overlay" onClick={() => setShowSubscriptionManager(false)}>
          <div className="account-modal" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal-header">
              <h2 className="account-modal-title">Subscription Management</h2>
              <button 
                className="account-modal-close-btn"
                onClick={() => setShowSubscriptionManager(false)}
              >
                ×
              </button>
            </div>
            <SubscriptionManager />
          </div>
        </div>
      )}

      {/* Dev Test Panel - Visible only in development mode - moved to top */}
    </div>
  )
}

/**
 * Main App wrapper with authentication provider
 */
function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <div className="app">
          <AppContent />
          <DevDebugPanel />
        </div>
      </ErrorBoundary>
    </AuthProvider>
  );
}

export default App
